import { useState } from "react";
import { BadgePercent, Check, MessageCircle, Send, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../context/LanguageContext";
import { api, errorMessage, TELEGRAM, whatsappLink } from "../../lib/api";
import { CopyCode } from "../CopyCode";

export const STUDENT_FIELDS = (t) => [
  ["full_name", t.reg.name, "text", "Dr. Ahmed Mohammed", true, "full"],
  ["specialty", t.reg.specialty, "text", "e.g. Cardiology, Internal Medicine...", true, "full"],
  ["email", t.reg.email, "email", "email@example.com", true, "full"],
  ["phone", t.reg.phone, "tel", "+966 5XXXXXXXX", true, "full", t.reg.phoneHint],
  ["institution", t.reg.institution, "text", "e.g. King Faisal Specialist Hospital...", false, "full"],
  ["country", t.reg.country, "text", "e.g. Saudi Arabia", false, ""],
  ["city", t.reg.city, "text", "e.g. Riyadh", false, ""],
  ["orcid", t.reg.orcid, "text", "0000-0001-2345-6789", false, "full"],
];

/** Shared student fields; `values` enables edit mode (prefilled). */
export const StudentFields = ({ values = {}, showDiscount = true }) => {
  const { t } = useLang();
  const [discount, setDiscount] = useState(null);
  const check = async (e) => {
    const code = e.target.value.trim();
    if (!code) return setDiscount(null);
    try { const r = await api.get(`/discounts/validate/${encodeURIComponent(code)}`); setDiscount({ ok: true, ...r.data }); }
    catch { setDiscount({ ok: false }); }
  };
  return (<>
    {STUDENT_FIELDS(t).map(([name, label, type, ph, req, cls, hint]) => (
      <label className={cls} key={name}><span>{label}{req && <span className="req"> *</span>}</span>
        <input name={name} type={type} placeholder={ph} required={req} defaultValue={values[name] || ""} pattern={name === "phone" ? "^\\+?[0-9\\s\\-]{9,16}$" : undefined} data-testid={`reg-${name.replace("_", "-")}-input`} />
        {hint && <span className="hint">{hint}</span>}
      </label>))}
    {showDiscount && <label className="full"><span><BadgePercent size={14} style={{ verticalAlign: -2 }} /> {t.reg.discount}</span>
      <input name="discount_code" placeholder="PROMO2026" defaultValue={values.discount_code || ""} onBlur={check} className="en" style={{ textTransform: "uppercase", letterSpacing: ".08em" }} data-testid="reg-discount-input" />
      {discount?.ok && <span className="hint" style={{ color: "var(--primary)", fontWeight: 700 }} data-testid="discount-valid">{t.reg.discountOk} {discount.percent}%</span>}
      {discount && !discount.ok && <span className="hint" style={{ color: "var(--danger)" }} data-testid="discount-invalid">{t.reg.discountBad}</span>}
    </label>}
  </>);
};

export const RegistrationForm = ({ opp, onDone, endpoint = "/registrations", headers, compact = false }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const payload = Object.fromEntries(new FormData(e.currentTarget).entries());
    try {
      const r = await api.post(endpoint, { ...payload, opportunity_id: opp.id }, { headers });
      setResult({ ...payload, ...r.data }); toast.success(t.reg.successTitle); onDone?.(r.data);
    } catch (err) { toast.error(errorMessage(err, t.reg.error)); }
    finally { setLoading(false); }
  };

  if (result) {
    const msg = `السلام عليكم، سجّلت في فرصة بحثية وأرغب باعتماد تسجيلي:\n• الفرصة: ${opp.title_en}\n• التخصص: ${opp.specialty_ar}\n• الاسم: ${result.full_name}\n• التخصص الدقيق: ${result.specialty}\n• رقم الواتساب: ${result.phone}\n• البريد: ${result.email}${result.discount_code ? `\n• كود الخصم: ${result.discount_code}` : ""}`;
    return (
      <div className="success-box" data-testid="registration-success">
        <span className="ring"><Check size={32} /></span>
        <h3>{t.reg.successTitle}</h3>
        {compact ? <p className="muted" style={{ marginTop: 8 }}>{t.coord.autoApproved} <CopyCode code={result.access_code} testId="student-code" /></p>
          : <p className="muted" style={{ marginTop: 8, lineHeight: 1.8 }}>{t.reg.successSub}</p>}
        {!compact && <a className="btn btn-whatsapp btn-block" style={{ marginTop: 18 }} href={whatsappLink(msg)} target="_blank" rel="noreferrer" data-testid="success-whatsapp"><MessageCircle size={18} />{t.reg.waButton}</a>}
      </div>
    );
  }

  return (
    <form className="form" onSubmit={submit} data-testid="registration-form">
      <StudentFields />
      <button className="btn btn-primary" type="submit" disabled={loading} data-testid="reg-submit-button">{loading ? <span className="loader" /> : t.reg.submit}</button>
    </form>
  );
};

export const RegistrationModal = ({ opp, onClose }) => {
  const { t } = useLang();
  const isFull = opp.status !== "open" || opp.remaining_seats <= 0;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="registration-modal">
        <button className="modal-close" onClick={onClose} data-testid="registration-modal-close"><X size={18} /></button>
        {isFull ? (
          <div className="success-box" data-testid="registration-full"><h3>{t.reg.fullTitle}</h3><p className="muted" style={{ marginTop: 8 }}>{t.reg.fullSub}</p>
            <a className="btn btn-outline" style={{ marginTop: 16 }} href={TELEGRAM} target="_blank" rel="noreferrer"><Send size={16} />Telegram</a></div>
        ) : (<>
          <h2>{t.reg.title}<span className="en">{opp.title_en}</span></h2>
          <p className="muted" style={{ fontSize: 14 }}>{t.reg.sub}</p>
          <RegistrationForm opp={opp} />
        </>)}
      </div>
    </div>
  );
};
